import { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { DocRequestsList } from "./DocRequestsList";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Checkbox } from "./ui/checkbox";
import { Switch } from "./ui/switch";
import { Tabs, TabsList, TabsTrigger } from "./ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "./ui/dialog";
import {
  ClipboardList, Plus, Loader2, CheckCircle2, Circle, Trash2, RotateCcw,
  CalendarClock, User, Truck, Flag, MessageSquare, Forward, GitBranch, Lock, Hourglass, Send, CheckCheck,
} from "lucide-react";
import { TaskChainDialog } from "./TaskChainDialog";
import { isOverdue, taskTiming } from "../lib/taskTiming";
import { TaskComments } from "./TaskComments";
import { TasksExport } from "./TasksExport";
import { SeenLine } from "./SeenLine";
import { useAuth } from "../context/AuthContext";
import { TaskReportDialog, TaskApproveDialog, TaskReportView } from "./TaskReview";

const PRIO = {
  high: { ar: "عالية", cls: "bg-red-100 text-red-700 border-red-200" },
  medium: { ar: "متوسطة", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  low: { ar: "منخفضة", cls: "bg-slate-100 text-slate-600 border-slate-200" },
};

export const TasksPanel = ({ canEdit = false }) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(() => { const v = sessionStorage.getItem("tasks_filter_once"); sessionStorage.removeItem("tasks_filter_once"); return v || "open"; });
  const [docReqCount, setDocReqCount] = useState(null);
  useEffect(() => { api.docRequests().then((r) => setDocReqCount(r.length)).catch(() => {}); }, []);
  const [person, setPerson] = useState("all");
  const [tq, setTq] = useState("");
  const [prio, setPrio] = useState("");
  const { company } = useAuth();
  const [busy, setBusy] = useState("");
  const [open, setOpen] = useState(false);
  const [assignable, setAssignable] = useState({ supervisors: [], employees: [] });
  const [form, setForm] = useState({ title: "", description: "", priority: "medium", due_date: "", private: false });
  const [picked, setPicked] = useState({}); // key `${type}:${id}` -> {type,id,name}
  const [saving, setSaving] = useState(false);
  const [commentTask, setCommentTask] = useState(null);
  const [parentTask, setParentTask] = useState(null);
  const [chainTask, setChainTask] = useState(null);
  const [reportTask, setReportTask] = useState(null);
  const [approveTask, setApproveTask] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.tasksList();
      setTasks(r.items || []);
    } catch {
      toast.error("تعذّر تحميل المهام");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openDialog = async (parent = null) => {
    setParentTask(parent);
    setForm(parent ? { title: parent.title, description: "", priority: parent.priority || "medium", due_date: parent.due_date || "", private: !!parent.private }
      : { title: "", description: "", priority: "medium", due_date: "", private: filter === "private" });
    setPicked({});
    setOpen(true);
    try {
      const r = await api.tasksAssignable();
      setAssignable({ supervisors: r.supervisors || [], employees: r.employees || [] });
    } catch {
      toast.error("تعذّر تحميل قائمة المستلِمين");
    }
  };

  const togglePick = (type, id, name) => {
    const key = `${type}:${id}`;
    setPicked((p) => {
      const n = { ...p };
      if (n[key]) delete n[key]; else n[key] = { type, id, name };
      return n;
    });
  };

  const submit = async () => {
    if (!form.title.trim()) { toast.error("أدخل عنوان المهمة"); return; }
    const assignees = Object.values(picked).map((x) => ({ type: x.type, id: x.id }));
    if (!assignees.length) { toast.error("اختر مستلِمًا واحدًا على الأقل"); return; }
    setSaving(true);
    try {
      await api.taskCreate({
        title: form.title.trim(),
        description: form.description.trim(),
        priority: form.priority,
        due_date: form.due_date || null,
        assignees,
        parent_id: parentTask?.id || null,
        private: form.private,
      });
      toast.success(parentTask ? "تم تحويل المهمة" : "تم إنشاء المهمة");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "تعذّر الإنشاء");
    } finally {
      setSaving(false);
    }
  };

  const doComplete = (tk) => (tk.can_approve ? setApproveTask(tk) : tk.can_report ? setReportTask(tk) : null);
  const doReopen = async (id) => {
    setBusy(id);
    try { await api.taskReopen(id); load(); } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر إعادة الفتح"); } finally { setBusy(""); }
  };
  const doDelete = async (id) => {
    if (!window.confirm("حذف هذه المهمة نهائياً؟")) return;
    setBusy(id);
    try { await api.taskDelete(id); setTasks((t) => t.filter((x) => x.id !== id)); } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحذف"); } finally { setBusy(""); }
  };

  const shown = useMemo(
    () => tasks.filter((t) => (filter === "private" ? !!t.private : !t.private && (filter === "all" ? true : filter === "overdue" ? isOverdue(t) : t.status === filter))
      && (person === "all" || (t.assignees || []).some((a) => `${a.type}:${a.id}` === person))
      && (!prio || t.priority === prio)
      && (!tq.trim() || `${t.title || ""} ${t.description || ""} ${(t.assignees || []).map((a) => a.name).join(" ")}`.toLowerCase().includes(tq.trim().toLowerCase()))),
    [tasks, filter, person, prio, tq]);
  const people = useMemo(() => {
    const m = new Map();
    tasks.forEach((t) => (t.assignees || []).forEach((a) => m.set(`${a.type}:${a.id}`, a.name)));
    return [...m.entries()].sort((a, b) => (a[1] || "").localeCompare(b[1] || "", "ar"));
  }, [tasks]);
  const personName = people.find(([k]) => k === person)?.[1] || "";
  const pub = tasks.filter((t) => !t.private);
  const openCount = pub.filter((t) => t.status === "open").length;
  const doneCount = pub.filter((t) => t.status === "done").length;
  const reviewCount = pub.filter((t) => t.status === "review").length;
  const overdueCount = pub.filter(isOverdue).length;
  const privCount = tasks.length - pub.length;

  return (
    <div className="space-y-4" data-testid="tasks-panel">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="h-10 w-10 rounded-2xl bg-blue-50 border border-blue-100 grid place-items-center">
            <ClipboardList className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <h2 className="font-heading font-bold text-slate-900 text-lg leading-tight">المهام</h2>
            <p className="text-xs text-slate-500">مهام بين المشرفين والمندوبين — الموكَّل إليه يرفع تقريراً ومنشئ المهمة يعتمدها ويقيّمها</p>
          </div>
        </div>
        {canEdit && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-task-btn" onClick={() => openDialog(null)} className="gap-1.5 rounded-xl">
                <Plus className="h-4 w-4" /> مهمة جديدة
              </Button>
            </DialogTrigger>
            <DialogContent dir="rtl" className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader className="text-right">
                <DialogTitle className="font-heading">{parentTask ? "تحويل المهمة" : "مهمة جديدة"}</DialogTitle>
                <DialogDescription>{parentTask ? `تبقى مرتبطة بالمهمة الأصلية «${parentTask.title}» ولا تكتمل الأصلية إلا بعد إنجازها` : "أسند مهمة لمشرف أو مندوب أو أكثر"}</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">العنوان</Label>
                  <Input data-testid="task-title-input" value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    placeholder="مثال: تسليم عهدة المندوب" className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs">الوصف (اختياري)</Label>
                  <Textarea data-testid="task-desc-input" value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    rows={3} className="mt-1" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">الأولوية</Label>
                    <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                      <SelectTrigger data-testid="task-priority-select" className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="high">عالية</SelectItem>
                        <SelectItem value="medium">متوسطة</SelectItem>
                        <SelectItem value="low">منخفضة</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">تاريخ الاستحقاق</Label>
                    <Input data-testid="task-due-input" type="date" value={form.due_date}
                      onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="mt-1" />
                  </div>
                </div>

                <div className="flex items-center justify-between rounded-xl border border-slate-200 px-3 py-2">
                  <span className="flex items-center gap-1.5 text-sm"><Lock className="h-4 w-4 text-slate-500" /> مهمة خاصة <span className="text-[11px] text-slate-400">— لا يراها إلا أنت والمستلِمون</span></span>
                  <Switch data-testid="task-private-switch" checked={form.private} disabled={!!parentTask?.private} onCheckedChange={(v) => setForm({ ...form, private: v })} />
                </div>
                <div>
                  <Label className="text-xs flex items-center gap-1.5"><User className="h-3.5 w-3.5" /> المشرفون</Label>
                  <div className="mt-1 rounded-xl border border-slate-200 max-h-40 overflow-y-auto divide-y">
                    {assignable.supervisors.length === 0 && <div className="p-3 text-xs text-slate-400 text-center">لا يوجد مشرفون أو مستخدمون</div>}
                    {assignable.supervisors.map((s) => {
                      const stype = s.type || "user";
                      const key = `${stype}:${s.id}`;
                      return (
                        <label key={key} data-testid={`pick-user-${s.id}`}
                          className="flex items-center gap-2 p-2 text-sm cursor-pointer hover:bg-slate-50">
                          <Checkbox checked={!!picked[key]} onCheckedChange={() => togglePick(stype, s.id, s.name)} />
                          <span className="flex-1">{s.name}</span>
                          {s.type === "employee" && <span className="text-[10px] text-blue-600 bg-blue-50 rounded px-1.5 py-0.5">مشرف تطبيق</span>}
                          {s.type === "user" && <span className="text-[10px] text-slate-600 bg-slate-100 rounded px-1.5 py-0.5">{s.external ? "مستخدم خارجي" : "مستخدم"}</span>}
                        </label>
                      );
                    })}
                  </div>
                </div>

                {Object.keys(picked).length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {Object.values(picked).map((x) => (
                      <Badge key={`${x.type}:${x.id}`} variant="secondary" className="gap-1">
                        {x.type === "user" ? <User className="h-3 w-3" /> : <Truck className="h-3 w-3" />}
                        {x.name}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>إلغاء</Button>
                <Button data-testid="save-task-btn" onClick={submit} disabled={saving} className="gap-1.5">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} إنشاء
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2" data-testid="tasks-person-bar">
        <Input data-testid="tasks-search" value={tq} onChange={(e) => setTq(e.target.value)} placeholder="بحث بعنوان المهمة أو الوصف أو المستلم" className="h-9 w-64" />
        <select data-testid="tasks-priority-filter" value={prio} onChange={(e) => setPrio(e.target.value)} className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm">
          <option value="">كل الأولويات</option>{Object.entries(PRIO).map(([k, v]) => <option key={k} value={k}>{v.ar}</option>)}
        </select>
        <select data-testid="tasks-person-filter" value={person} onChange={(e) => setPerson(e.target.value)}
          className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm min-w-48">
          <option value="all">كل الموظفين</option>
          {people.map(([k, n]) => <option key={k} value={k}>{n}</option>)}
        </select>
        {person !== "all" && <TasksExport tasks={shown} personName={personName} company={company}
          tabLabel={filter === "open" ? "المهام الجارية" : filter === "review" ? "بانتظار الاعتماد" : filter === "done" ? "المهام المكتملة" : "كل المهام"} />}
        {person === "all" && <span className="text-xs text-slate-400">اختر موظفاً لتصدير مهامه (Excel / PDF)</span>}
      </div>
      <Tabs value={filter} onValueChange={setFilter} className="-mx-1 overflow-x-auto px-1 pb-1">
        <TabsList data-testid="task-filter-tabs" className="w-max">
          <TabsTrigger value="open" data-testid="task-filter-open">الجارية ({openCount})</TabsTrigger>
          <TabsTrigger value="review" data-testid="task-filter-review" className="data-[state=active]:text-violet-700">بانتظار الاعتماد ({reviewCount})</TabsTrigger>
          <TabsTrigger value="done" data-testid="task-filter-done">المكتملة ({doneCount})</TabsTrigger>
          <TabsTrigger value="overdue" data-testid="task-filter-overdue" className="data-[state=active]:text-red-600">لم تكتمل ({overdueCount})</TabsTrigger>
          <TabsTrigger value="all" data-testid="task-filter-all">الكل ({pub.length})</TabsTrigger>
          <TabsTrigger value="private" data-testid="task-filter-private" className="gap-1 data-[state=active]:text-slate-900"><Lock className="h-3.5 w-3.5" /> خاصة ({privCount})</TabsTrigger>
          <TabsTrigger value="docs" data-testid="task-filter-docs" className="data-[state=active]:text-amber-700">طلبات الوثائق{docReqCount != null ? ` (${docReqCount})` : ""}</TabsTrigger>
        </TabsList>
      </Tabs>

      {filter === "docs" ? <DocRequestsList onCount={setDocReqCount} /> : loading ? (
        <div className="py-16 grid place-items-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : shown.length === 0 ? (
        <div className="py-16 text-center text-slate-400" data-testid="tasks-empty">
          <ClipboardList className="h-10 w-10 mx-auto mb-2 opacity-40" />
          لا توجد مهام
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {shown.map((tk) => {
            const done = tk.status === "done";
            const p = PRIO[tk.priority] || PRIO.medium;
            return (
              <motion.div key={tk.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                data-testid={`task-card-${tk.id}`}
                className={`rounded-2xl border p-4 transition-colors ${done ? "bg-emerald-50/40 border-emerald-100" : "bg-white border-slate-200"}`}>
                <div className="flex items-start gap-3">
                  <button
                    data-testid={`task-toggle-${tk.id}`}
                    disabled={done || !(tk.can_approve || tk.can_report) || busy === tk.id}
                    onClick={() => doComplete(tk)}
                    title={done ? "مكتملة ومعتمدة" : tk.can_approve ? "اعتماد وإغلاق" : tk.can_report ? "رفع تقرير الإنجاز" : tk.status === "review" ? "بانتظار اعتماد منشئ المهمة" : "لا تملك صلاحية"}
                    className={`mt-0.5 shrink-0 transition ${!done && (tk.can_approve || tk.can_report) ? "hover:scale-110" : "cursor-default"}`}>
                    {busy === tk.id ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                      : done ? <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                        : tk.status === "review" ? <Hourglass className="h-5 w-5 text-violet-500" />
                          : <Circle className="h-5 w-5 text-slate-300" />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <button type="button" onClick={() => setCommentTask(tk)} data-testid={`task-open-${tk.id}`} className={`text-right font-heading font-bold text-slate-900 hover:text-blue-700 ${done ? "line-through text-slate-400" : ""}`}>{tk.private && <Lock className="inline h-3.5 w-3.5 me-1 text-slate-500" data-testid={`task-private-${tk.id}`} />}{tk.title}</button>
                    {tk.description && <p className="text-xs text-slate-500 mt-0.5 whitespace-pre-wrap">{tk.description}</p>}
                    <div className="flex flex-wrap items-center gap-1.5 mt-2">
                      <Badge variant="outline" className={`gap-1 text-[11px] ${p.cls}`}><Flag className="h-3 w-3" />{p.ar}</Badge>
                      {tk.due_date && <Badge variant="outline" className="gap-1 text-[11px]"><CalendarClock className="h-3 w-3" />{tk.due_date}</Badge>}
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${taskTiming(tk)[1]}`} data-testid={`task-timing-${tk.id}`}>{taskTiming(tk)[0]}</span>
                      {tk.status === "review" && <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-bold text-violet-700" data-testid={`task-review-badge-${tk.id}`}>بانتظار الاعتماد</span>}
                      {tk.sub_total ? <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-bold text-indigo-700" data-testid={`task-subs-${tk.id}`}>محوّلة {tk.sub_done || 0}/{tk.sub_total}</span> : null}
                      {(tk.assignees || []).map((a) => (
                        <Badge key={`${a.type}:${a.id}`} variant="secondary" className="gap-1 text-[11px]">
                          {a.type === "user" ? <User className="h-3 w-3" /> : <Truck className="h-3 w-3" />}{a.name}
                        </Badge>
                      ))}
                    </div>
                    <div className="flex items-center justify-between mt-2 text-[11px] text-slate-400">
                      <span>بواسطة {tk.created_by_name || "—"}</span>
                      {done && (tk.completed_by_name ? <span className="text-emerald-600">✓ أنجزها {tk.completed_by_name}</span>
                        : tk.review && <span className="text-slate-500">أغلقها المنشئ بدون تقرير</span>)}
                    </div>
                    <TaskReportView task={tk} />
                    <SeenLine items={tk.seen_by} showPending={!done} testId={`task-seen-${tk.id}`} />
                    {tk.can_report && (
                      <Button size="sm" data-testid={`task-report-btn-${tk.id}`} onClick={() => setReportTask(tk)} className="mt-2 h-7 gap-1 rounded-lg text-[11px]">
                        <Send className="h-3.5 w-3.5" /> رفع تقرير الإنجاز</Button>)}
                    {tk.can_approve && (
                      <Button size="sm" data-testid={`task-approve-open-${tk.id}`} onClick={() => setApproveTask(tk)}
                        className={`mt-2 ${tk.can_report ? "ms-2" : ""} h-7 gap-1 rounded-lg text-[11px] ${tk.status === "review" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-slate-700 hover:bg-slate-800"}`}>
                        <CheckCheck className="h-3.5 w-3.5" /> {tk.status === "review" ? "مراجعة التقرير والاعتماد" : "إغلاق وتقييم"}</Button>)}
                    <button data-testid={`task-comments-${tk.id}`} onClick={() => setCommentTask(tk)}
                      className="mt-2 inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-blue-600 transition">
                      <MessageSquare className="h-3.5 w-3.5" /> تعليقات{tk.comment_count ? ` (${tk.comment_count})` : ""}
                    </button>
                    {(tk.parent_id || tk.sub_total) ? (
                      <button data-testid={`task-chain-${tk.id}`} onClick={() => setChainTask(tk)}
                        className="mt-2 ms-3 inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-indigo-600 transition">
                        <GitBranch className="h-3.5 w-3.5" /> التسلسل
                      </button>) : null}
                    {canEdit && !done && tk.can_complete && (
                      <button data-testid={`task-forward-${tk.id}`} onClick={() => openDialog(tk)}
                        className="mt-2 ms-3 inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-blue-600 transition">
                        <Forward className="h-3.5 w-3.5" /> تحويل المهمة
                      </button>)}
                  </div>
                  {canEdit && (
                    <button data-testid={`task-delete-${tk.id}`} onClick={() => doDelete(tk.id)}
                      className="text-slate-300 hover:text-red-500 transition shrink-0" title="حذف">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
                {done && tk.can_reopen && (
                  <button data-testid={`task-reopen-${tk.id}`} onClick={() => doReopen(tk.id)}
                    className="mt-2 text-[11px] text-slate-500 hover:text-blue-600 inline-flex items-center gap-1">
                    <RotateCcw className="h-3 w-3" /> إعادة فتح
                  </button>
                )}
              </motion.div>
            );
          })}
        </div>
      )}
      <TaskChainDialog task={chainTask} onClose={() => setChainTask(null)} />
      <TaskReportDialog task={reportTask} onClose={() => setReportTask(null)} onDone={load} />
      <TaskApproveDialog task={approveTask} onClose={() => setApproveTask(null)} onDone={load} />
      <TaskComments task={commentTask} mode="admin" onClose={() => setCommentTask(null)} onPosted={load} />
    </div>
  );
};
